import type { EventLogEntityFilter, ListEventLogsParams } from 'api/models/api';
import type { FilterValue } from 'common/models';
import { useGetEventLogsQuery } from 'common/queries';
import { useUserTimezone } from 'common/useCompanyTimezoneId';
import Modal from 'components/Modal/Modal';
import { defaultEventLogQuery } from 'helpers/auditHelper';
import { useCustomTranslation } from 'helpers/translationHelper';
import { useUsers } from 'helpers/usersHelper';
import { useCallback, useState } from 'react';

import EventLogTable from './EventLogTable';

interface EventLogModalProps {
  open: boolean;
  onClose: () => void;
  entityId?: string;
  entityType?: EventLogEntityFilter;
}

const EventLogModal = ({ onClose, open, entityId, entityType }: EventLogModalProps) => {
  const { tAudit } = useCustomTranslation();

  const { users } = useUsers(open);
  const timezoneId = useUserTimezone();

  const [tableParams, setTableParams] = useState<ListEventLogsParams>(defaultEventLogQuery);

  const queryParams = {
    ...tableParams,
    entityId,
    entityType
  };

  const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } = useGetEventLogsQuery(
    queryParams,
    open
  );

  const onTableChange = useCallback(
    (params: ListEventLogsParams, filters: Partial<Record<string, FilterValue | null>>) => {
      const updatedTableParams = {
        ...params,
        actorUserId: filters.actorName?.join(',')
      };

      setTableParams(updatedTableParams);
    },
    [setTableParams]
  );

  return (
    <Modal
      onClose={onClose}
      open={open}
      width={900}
      className="EventLogModal"
      title={tAudit('event_log')}
    >
      <EventLogTable
        data={data}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        onTableChange={(filters) => onTableChange(tableParams, filters)}
        timezoneId={timezoneId}
        users={users}
      />
    </Modal>
  );
};

export default EventLogModal;
